import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import styles from "./css/RoulettePage.module.css";
import { buildApiUrl } from "../config/runtime";

const TILE_WIDTH = 220;
const TILE_GAP = 16;
const TILE_STEP = TILE_WIDTH + TILE_GAP;

function buildTrack(prizes, loops, winningIndex) {
  const items = [];
  for (let i = 0; i < loops; i += 1) {
    prizes.forEach((prize) => items.push(prize));
  }
  const finalIndex = prizes.length * (loops - 1) + winningIndex;
  return { items, finalIndex };
}

function formatTimeLeft(ms) {
  if (!ms || ms <= 0) return "";
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h > 0) return `${h}ч ${m}м`;
  return `${m}м`;
}

function RouletteWheel({
  title,
  tag,
  price,
  prizes,
  variant,
  remaining,
  nextAt,
  cooldownHours,
  onSpin,
  paymentLink,
  paymentCost,
  paymentOptions,
  loading
}) {
  const windowRef = useRef(null);
  const trackRef = useRef(null);
  const pendingPrizeRef = useRef(null);
  const spinTimeoutRef = useRef(null);

  const [spinning, setSpinning] = useState(false);
  const [resultPrize, setResultPrize] = useState(null);
  const [history, setHistory] = useState([]);
  const [spinError, setSpinError] = useState(null);
  const [poolOpen, setPoolOpen] = useState(false);

  const payOptions = Array.isArray(paymentOptions) ? paymentOptions.filter(Boolean) : [];
  const prizePool = useMemo(() => {
    const source = Array.isArray(prizes)
      ? prizes
      : prizes && typeof prizes === "object"
      ? Object.values(prizes)
      : [];

    const seen = new Set();
    const out = [];
    for (const item of source) {
      if (!item || typeof item !== "object") continue;
      const key = String(item.id || item.label || `${out.length}`);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
    return out;
  }, [prizes]);

  const [trackItems, setTrackItems] = useState(() => buildTrack(prizePool, 5, 0).items);

  const showPayOptions = variant === "paid" && payOptions.length > 0;
  const hasLockedOptions = showPayOptions && payOptions.some((option) => !option?.link);
  const canSpin = !spinning && !loading && remaining > 0 && prizePool.length > 0;

  const getTileMetrics = useCallback(() => {
    const trackEl = trackRef.current;
    const firstTile = trackEl?.children?.[0];
    if (trackEl && firstTile) {
      const tileWidth = firstTile.getBoundingClientRect().width || TILE_WIDTH;
      const computed = getComputedStyle(trackEl);
      const gapValue = computed.gap || computed.columnGap || `${TILE_GAP}px`;
      const gap = Number.parseFloat(gapValue) || TILE_GAP;
      return { tileWidth, step: tileWidth + gap };
    }
    return { tileWidth: TILE_WIDTH, step: TILE_STEP };
  }, []);

  const getCenterOffset = useCallback(() => {
    const width = windowRef.current?.getBoundingClientRect()?.width || 900;
    const { tileWidth } = getTileMetrics();
    return width / 2 - tileWidth / 2;
  }, [getTileMetrics]);

  const calcTranslate = useCallback((index) => {
    const centerOffset = getCenterOffset();
    const { step } = getTileMetrics();
    return -(index * step) + centerOffset;
  }, [getCenterOffset, getTileMetrics]);

  const applyTransform = useCallback((value, duration) => {
    const trackEl = trackRef.current;
    if (!trackEl) return;
    trackEl.style.transition =
      duration && duration > 0
        ? `transform ${duration}ms cubic-bezier(0.12, 0.7, 0.1, 0.98)`
        : "none";
    trackEl.style.transform = `translateX(${value}px)`;
  }, []);

  useEffect(() => {
    const source = prizePool.length > 0 ? prizePool : [];
    const startIndex = Math.max(0, Math.floor(source.length / 2));
    setTrackItems(buildTrack(source, 4, 0).items);
    requestAnimationFrame(() => applyTransform(calcTranslate(startIndex), 0));
    return () => {
      if (spinTimeoutRef.current) {
        clearTimeout(spinTimeoutRef.current);
        spinTimeoutRef.current = null;
      }
    };
  }, [applyTransform, calcTranslate, prizePool]);

  const finalizeSpin = () => {
    if (!spinning) return;
    const prize = pendingPrizeRef.current;
    setSpinning(false);
    setResultPrize(prize);
    if (prize) {
      setHistory((prev) => [prize, ...prev].slice(0, 4));
    }
    if (spinTimeoutRef.current) {
      clearTimeout(spinTimeoutRef.current);
      spinTimeoutRef.current = null;
    }
  };

  const spin = async () => {
    if (!canSpin || !onSpin) return;
    setSpinError(null);
    setSpinning(true);

    if (prizePool.length === 0) {
      setSpinning(false);
      setSpinError("Нет призов");
      return;
    }

    let prizeFromServer = null;
    try {
      prizeFromServer = await onSpin();
    } catch (err) {
      setSpinning(false);
      setSpinError(err?.message || "Ошибка запуска");
      return;
    }

    if (!prizeFromServer) {
      setSpinning(false);
      setSpinError("Недоступно");
      return;
    }

    const winningIndex = prizePool.findIndex((p) => p.id === prizeFromServer.id);
    const safeIndex = winningIndex >= 0 ? winningIndex : Math.floor(Math.random() * prizePool.length);
    const loops = 7;
    const { items, finalIndex } = buildTrack(prizePool, loops, safeIndex);
    const startIndex = Math.max(0, Math.floor(prizePool.length / 2));
    const spinDuration = 4200 + Math.floor(Math.random() * 2200);

    setResultPrize(null);
    pendingPrizeRef.current = prizeFromServer;
    setTrackItems(items);

    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));

    const startTranslate = calcTranslate(startIndex);
    applyTransform(startTranslate, 0);
    if (trackRef.current) {
      void trackRef.current.offsetHeight;
    }

    const finalTranslate = calcTranslate(finalIndex);
    applyTransform(finalTranslate, spinDuration);
    spinTimeoutRef.current = setTimeout(() => finalizeSpin(), spinDuration + 200);
  };

  const handleTransitionEnd = (event) => {
    if (event.propertyName !== "transform") return;
    if (!spinning) return;
    finalizeSpin();
  };

  return (
    <section className={`${styles.wheelCard} ${styles[variant]}`}>
      <div className={styles.wheelHeader}>
        <div>
          <p className={styles.wheelTag}>{tag}</p>
          <h2>{title}</h2>
        </div>
        <span className={styles.wheelPrice}>{price}</span>
      </div>

      <div className={styles.wheelMeta}>
        <div className={styles.spinsInfo}>
          <div className={styles.spinsText}>Осталось: {loading ? "—" : remaining}</div>
          {remaining <= 0 && variant === "free" ? (
            <div className={styles.spinHint}>
              Каждые {cooldownHours || 24} часа
              {nextAt ? ` · через ${formatTimeLeft(nextAt - Date.now())}` : ""}
            </div>
          ) : null}
          {spinError ? <div className={styles.spinHint}>{spinError}</div> : null}
        </div>
        <div className={styles.resultPill}>{resultPrize ? resultPrize.label : "—"}</div>
      </div>

      <div className={styles.window} ref={windowRef}>
        <div className={styles.pointer}>
          <div className={styles.pointerLine} />
          <div className={styles.pointerTip} />
        </div>
        <div className={styles.track} ref={trackRef} onTransitionEnd={handleTransitionEnd}>
          {trackItems.map((item, index) => (
            <div
              key={`${item.id || "prize"}-${index}`}
              className={`${styles.tile} ${styles[item.rarity] || ""}`}
              style={{ "--accent": item.accent }}
            >
              <div className={styles.tileGlow} />
              <div className={styles.tileIcon}>
                <img src={item.icon} alt="" />
              </div>
              <div className={styles.tileLabel}>{item.label}</div>
              <div className={styles.tileMeta}>{String(item.rarity || "common").toUpperCase()}</div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.wheelFooter}>
        <button className={styles.spinButton} onClick={spin} disabled={!canSpin}>
          {spinning ? "Крутим..." : "Крутить"}
        </button>
        {remaining <= 0 && variant === "paid" && paymentLink && !showPayOptions ? (
          <a className={styles.payButton} href={paymentLink} target="_blank" rel="noreferrer">
            Оплатить {paymentCost ? `${paymentCost} ₽` : ""}
          </a>
        ) : null}
      </div>

      {showPayOptions ? (
        <div className={styles.payOptions}>
          {payOptions.map((option, index) => {
            const content = (
              <>
                <span>{option.label || "Пакет"}</span>
                <span>{option.cost ? `${option.cost} ₽` : ""}</span>
              </>
            );

            if (option.link) {
              return (
                <a
                  key={option.id || option.link || index}
                  className={styles.payOption}
                  href={option.link}
                  target="_blank"
                  rel="noreferrer"
                >
                  {content}
                </a>
              );
            }

            return (
              <div
                key={option.id || option.label || index}
                className={`${styles.payOption} ${styles.payOptionDisabled}`}
                title="Доступно по ссылке из бота"
              >
                {content}
              </div>
            );
          })}
          {hasLockedOptions ? (
            <div className={styles.payHint}>Оплата доступна по ссылке из бота</div>
          ) : null}
        </div>
      ) : null}

      <div className={styles.historyRow}>
        {history.length > 0 ? (
          history.map((item, idx) => (
            <span key={`${item.id || "history"}-${idx}`} className={styles.historyChip}>
              {item.label}
            </span>
          ))
        ) : (
          <span className={styles.historyEmpty}>История пуста</span>
        )}
      </div>

      <div className={styles.poolSection}>
        <button
          type="button"
          className={styles.poolLaunchButton}
          onClick={() => setPoolOpen(true)}
          disabled={prizePool.length === 0}
        >
          <span className={styles.poolLaunchTitle}>Что может выпасть</span>
          <span className={styles.poolLaunchMeta}>
            {prizePool.length > 0 ? `${prizePool.length} призов` : "Призы не загружены"}
          </span>
        </button>
      </div>

      {poolOpen && prizePool.length > 0 ? (
        <div className={styles.poolModalOverlay} onClick={() => setPoolOpen(false)}>
          <div className={styles.poolModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.poolModalHead}>
              <div className={styles.poolModalTitle}>Все призы: {title}</div>
              <button
                type="button"
                className={styles.poolModalClose}
                onClick={() => setPoolOpen(false)}
                aria-label="Закрыть список призов"
              >
                &times;
              </button>
            </div>
            <div className={styles.poolModalGrid}>
              {prizePool.map((item, idx) => (
                <div
                  key={`${item.id || item.label || "modal-pool"}-${idx}`}
                  className={`${styles.poolCard} ${styles[item.rarity] || ""}`}
                  style={{ "--accent": item.accent || "#7CE7FF" }}
                >
                  <div className={styles.poolGlow} />
                  <div className={styles.poolIcon}>
                    {item.icon ? <img src={item.icon} alt="" /> : null}
                  </div>
                  <div className={styles.poolText}>
                    <div className={styles.poolLabel}>{item.label || "Приз"}</div>
                    <div className={styles.poolRarity}>{String(item.rarity || "common").toUpperCase()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default function RoulettePage() {
  const [loading, setLoading] = useState(true);
  const [freePrizes, setFreePrizes] = useState([]);
  const [paidPrizes, setPaidPrizes] = useState([]);
  const [state, setState] = useState({
    free: { remaining: 0, nextAt: null, cooldownHours: 24 },
    paid: { remaining: 0 }
  });
  const [payment, setPayment] = useState(null);

  const token = (() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("token") || "";
  })();
  const demoMode = !token;

  const normalizePrizeList = (value) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") return Object.values(value);
    return [];
  };

  const loadState = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(buildApiUrl("/chat/roulette/state"), {
        params: demoMode ? { demo: 1 } : { token }
      });
      const data = res.data || {};
      setState(
        data.state || {
          free: { remaining: 0, nextAt: null, cooldownHours: 24 },
          paid: { remaining: 0 }
        }
      );
      setFreePrizes(normalizePrizeList(data.prizes?.free));
      setPaidPrizes(normalizePrizeList(data.prizes?.paid));
      setPayment(data.payment || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [demoMode, token]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  const spin = async (type) => {
    try {
      const res = await axios.post(
        buildApiUrl("/chat/roulette/spin"),
        demoMode ? { type, demo: true } : { token, type }
      );
      const data = res.data || {};
      if (data.state) setState(data.state);
      if (data.payment !== undefined) setPayment(data.payment || null);
      return data.prize || null;
    } catch (err) {
      const message = err?.response?.data?.error || "Ошибка запуска";
      throw new Error(message);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <header className={styles.header}>
          <div>
            <p className={styles.kicker}>Рулетки</p>
            <h1>Выбери и крути</h1>
          </div>
          <div className={styles.badges}>
            <span className={styles.badge}>Ежедневная</span>
            <span className={styles.badge}>Платная</span>
          </div>
        </header>

        <div className={styles.grid}>
          <RouletteWheel
            title="Бесплатная рулетка"
            tag="Ежедневная"
            price="FREE"
            prizes={freePrizes}
            variant="free"
            remaining={state.free.remaining}
            nextAt={state.free.nextAt}
            cooldownHours={state.free.cooldownHours}
            loading={loading}
            onSpin={() => spin("free")}
          />
          <RouletteWheel
            title="Платная рулетка"
            tag="Премиум"
            price="PRO"
            prizes={paidPrizes}
            variant="paid"
            remaining={state.paid.remaining}
            loading={loading}
            onSpin={() => spin("paid")}
            paymentLink={payment?.link || null}
            paymentCost={payment?.cost || null}
            paymentOptions={payment?.options || []}
          />
        </div>
      </div>
    </div>
  );
}
